import { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState, TypedDispatch } from 'redux/store'
import { makeOrderContactValidationSchema } from './contact-utils'
import { OrderContactSlice } from './contact-types'
import orderContactSlice from './contact-slice'
import useConfig from 'modules/config/state/use-config'

const useOrderContact = () => {
  const config = useConfig()
  const dispatch = useDispatch<TypedDispatch<RootState>>()
  const value = useSelector((state: RootState) => state.order.contact)

  /**
   * IMPORTANT: contact.phone requirement mirrors the homephone question only in non-embedded flows.
   * In embedded flows, we validate homephone via contact answers schema and
   * keep contact.phone optional.
   */
  const isPhoneRequired = useMemo(
    () =>
      !config.isEmbedded &&
      config.questions.contact.some(
        (question) => question.tag === 'homephone' && question.is_required,
      ),
    [config],
  )

  const schema = useMemo(() => makeOrderContactValidationSchema(isPhoneRequired), [isPhoneRequired])

  const setValue = useCallback(
    (value: OrderContactSlice) => {
      dispatch(orderContactSlice.actions.setValue(value))
    },
    [dispatch],
  )

  const updateField = useCallback(
    <K extends keyof OrderContactSlice>(field: K, value: OrderContactSlice[K]) => {
      dispatch(orderContactSlice.actions.updateField({ field, value }))
    },
    [dispatch],
  )

  return useMemo(
    () => ({
      value,
      schema,
      setValue,
      updateField,
      isPhoneRequired,
    }),
    [value, schema, setValue, updateField, isPhoneRequired],
  )
}

export default useOrderContact
